// Données des tuiles : valeur et liste explicative partagent la même source et les mêmes filtres.
// Contrat réel des routes /indicateurs (packages/contrats/src/indicateurs.ts) : couverture en objet,
// filtres service_id et arrondissement_id, liste limitée par « limite » avec le total de lignes.
import type { ListeIndicateur, ValeurIndicateur } from '@cc/contrats';
import { LIBELLES_ETATS_COURRIER, LIBELLES_ETATS_DOSSIER } from '@cc/contrats/ui-metier';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { nomAppartenance, useDelegations, useModules, useUtilisateurs } from '../../api/requetes';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { LIBELLES_DOMAINES } from '../../session/permissions';
import { delegationsArrivantAExpiration } from '../administration/delegations';
import { LIBELLES_TYPES } from '../administration/FormulaireDelegation';
import { nomUtilisateur } from '../administration/PageUtilisateurs';
import type { DefinitionTuile } from './catalogue';
import { calculerPeriode, type CodePeriode } from './periode';

export interface FiltresTableau {
  periode: CodePeriode;
  /** Identifiant d'arrondissement, vide pour tous. */
  arrondissement: string;
}

export interface LigneExplicative {
  id: string;
  reference: string;
  objet: string;
  detail?: string;
  etat?: string;
  href?: string;
}

export function parametresIndicateur(code: string, filtres: FiltresTableau) {
  const p = calculerPeriode(filtres.periode);
  return {
    code,
    debut: p.debut,
    fin: p.fin,
    arrondissement_id: filtres.arrondissement || undefined,
  };
}

/** Lignes chargées dans une liste explicative (maximum de l'API) ; au-delà, le total et l'export les comptent toutes. */
export const LIMITE_LISTE_INDICATEUR = 1000;

export function useValeurIndicateur(t: DefinitionTuile, filtres: FiltresTableau, actif: boolean) {
  return useQuery({
    queryKey: ['indicateurs', 'valeur', t.code, filtres],
    queryFn: ({ signal }) => requete<ValeurIndicateur>('/indicateurs/valeur', { signal, parametres: parametresIndicateur(t.code, filtres) }),
    enabled: actif && t.source === 'indicateur',
    retry: false,
  });
}

/** Libellé affiché après la valeur selon l'unité de l'indicateur. */
export function libelleUnite(unite: string | null | undefined): string {
  if (unite === 'jours') return ' jours';
  if (unite === 'taux') return ' %';
  if (unite === 'note') return ' sur 5';
  // Indicateurs des recettes (lot L2-A) : montants en francs CFA.
  if (unite === 'montant') return ' F';
  return '';
}

const LIBELLES_EMPLACEMENT: Record<string, string> = { disponible: 'Disponible', attribue: 'Attribué', reserve: 'Réservé', hors_service: 'Hors service' };
const LIBELLES_ETATS_RECLAMATION: Record<string, string> = { recue: 'Reçue', en_instruction: 'En instruction', repondue: 'Répondue', cloturee: 'Clôturée' };

/**
 * Ligne d'une liste explicative : colonnes des calculateurs (numero, objet ou demarche, service,
 * correspondant, etat, objet_type, mesure). Une réclamation sensible hors habilitation arrive avec
 * un identifiant nul : elle est comptée mais ni nommée ni liée.
 */
function ligneMetier(e: ListeIndicateur['elements'][number], index: number): LigneExplicative {
  const l = e as Record<string, unknown>;
  const texte = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const id = typeof l.id === 'string' && l.id ? l.id : null;
  const estReclamation = l.objet_type === 'reclamation' || 'categorie' in l;
  const estDossier = !estReclamation && (l.objet_type === 'dossier' || 'demarche' in l || l.objet_type === 'demarche');
  // MAR-01 (lot L2-B) : une ligne par emplacement (référence, marché, zone, état occupé ou libre).
  const estEmplacement = l.objet_type === 'emplacement';
  // Lot L3-B : réalisation de tournée (PRO-01), mesure corrective (CTR-01) et constat (CTR-02) ouvrent la tournée ou l'ordre de mission.
  const estProprete = l.objet_type === 'realisation_tournee';
  const estControle = l.objet_type === 'mesure_corrective' || l.objet_type === 'constat';
  // Lot L4-B : sections du budget (BUD-01), liquidations (BUD-02) et paiements (DEP-01) ouvrent les écrans du budget.
  const lienBudget = l.objet_type === 'budget' ? '/budget' : l.objet_type === 'liquidation' || l.objet_type === 'paiement_depense' ? '/budget/depense' : null;
  const etat = texte(l.etat);
  const mesure = typeof l.mesure === 'number' ? l.mesure : null;
  // Lot L4-A : un bien (PAT-01) ou un équipement (EQU-01) ouvre sa fiche.
  if (id && (l.objet_type === 'bien' || l.objet_type === 'equipement')) return { id, reference: texte(l.numero) ?? 'sans numéro', objet: texte(l.designation) ?? texte(l.nom) ?? 'Objet non renseigné', detail: texte(l.motif) ?? (typeof l.heures_reservees === 'number' ? `${l.heures_reservees.toLocaleString('fr-FR')} h réservées sur ${Number(l.heures_offertes).toLocaleString('fr-FR')} h ouvertes` : undefined), etat: texte(l.categorie), href: l.objet_type === 'bien' ? `/patrimoine/biens/${id}` : `/equipements/${id}` };
  const libellesEtat = estReclamation ? LIBELLES_ETATS_RECLAMATION : estDossier ? LIBELLES_ETATS_DOSSIER : estEmplacement ? LIBELLES_EMPLACEMENT : LIBELLES_ETATS_COURRIER;
  const occupation = estEmplacement ? (l.occupe === 'oui' ? `Occupé${texte(l.attribution) ? ` (${texte(l.attribution)})` : ''}` : 'Libre') : null;
  return {
    id: id ?? `masquee-${index}`,
    reference: id === null ? 'Réclamation sensible (masquée)' : (texte(l.numero) ?? texte(l.code) ?? 'sans numéro'),
    objet: texte(l.objet) ?? texte(l.demarche) ?? texte(l.categorie) ?? texte(l.libelle) ?? (estEmplacement ? [texte(l.marche), texte(l.zone)].filter(Boolean).join(' · ') : undefined) ?? 'Objet non renseigné',
    detail: texte(l.service) ?? texte(l.correspondant) ?? texte(l.demandeur) ?? texte(l.motif) ?? texte(l.description) ?? (typeof l.date === 'string' ? l.date : undefined),
    etat: occupation ?? (estProprete || estControle ? (texte(l.echeance) ? `échéance ${texte(l.echeance)}` : (etat ?? undefined)) : etat ? (libellesEtat[etat] ?? etat) : mesure !== null ? `mesure ${mesure.toLocaleString('fr-FR')}` : undefined),
    href: id === null ? undefined : estReclamation ? `/reclamations/${id}` : l.objet_type === 'demarche' ? `/demarches/${id}` : estEmplacement ? `/marches/emplacements/${id}` : estProprete ? `/proprete/tournees/${texte(l.tournee_id) ?? ''}` : estControle ? `/inspections/ordres/${texte(l.ordre_id) ?? ''}` : lienBudget ? lienBudget : estDossier ? `/dossiers/${id}` : `/courriers/${id}`,
  };
}

export function useListeIndicateur(t: DefinitionTuile | undefined, filtres: FiltresTableau, actif: boolean) {
  return useQuery({
    queryKey: ['indicateurs', 'liste', t?.code, filtres],
    queryFn: ({ signal }) =>
      requete<ListeIndicateur>('/indicateurs/liste', { signal, parametres: { ...parametresIndicateur(t?.code ?? '', filtres), limite: LIMITE_LISTE_INDICATEUR } }).then((r) => ({
        calcule_le: r.calcule_le ?? null,
        total: r.total,
        couverture: r.couverture.etat,
        lignes: r.elements.map((e, i) => ligneMetier(e, i)),
      })),
    enabled: actif && t?.source === 'indicateur',
    retry: false,
  });
}

/** Lignes des tuiles du socle, calculées à partir des routes du lot L0-A. */
export function useListeSocle(code: string | undefined, actif: boolean) {
  const { contexte } = useSession();
  const moi = contexte?.appartenance_active?.id;
  const delegations = useDelegations();
  const utilisateurs = useUtilisateurs(actif && (code === 'SOC-ATT' || code === 'SOC-DEL'));
  const modules = useModules();
  if (!actif || !code?.startsWith('SOC-')) return { chargement: false, erreur: null, lignes: null as LigneExplicative[] | null };
  if (code === 'SOC-DEL') {
    const lignes: LigneExplicative[] = delegationsArrivantAExpiration(delegations.data ?? []).map((d) => ({
      id: d.id,
      reference: date(d.fin),
      objet: `${LIBELLES_TYPES[d.type] ?? d.type} de ${nomAppartenance(utilisateurs.data, d.delegant_appartenance_id, moi)} à ${nomAppartenance(utilisateurs.data, d.delegataire_appartenance_id, moi)}`,
      detail: d.domaines.map((x) => LIBELLES_DOMAINES[x] ?? x).join(', '),
      etat: 'Expire bientôt',
      href: '/administration/delegations',
    }));
    return { chargement: delegations.isPending, erreur: delegations.error, lignes: delegations.data ? lignes : null };
  }
  if (code === 'SOC-ATT') {
    const lignes: LigneExplicative[] = (utilisateurs.data ?? []).flatMap((u) =>
      u.roles
        .filter((r) => r.statut === 'en_attente_validation')
        .map((r) => ({ id: `${u.appartenance_id}-${r.role_id}`, reference: r.code, objet: `${r.libelle} pour ${nomUtilisateur(u)}`, etat: 'À valider', href: '/administration/utilisateurs' })),
    );
    return { chargement: utilisateurs.isPending, erreur: utilisateurs.error, lignes: utilisateurs.data ? lignes : null };
  }
  const lignes: LigneExplicative[] = (modules.data ?? [])
    .filter((m) => m.actif)
    .map((m) => ({ id: m.module, reference: m.module, objet: m.libelle, detail: m.active_le ? `Actif depuis le ${date(m.active_le)}` : undefined, etat: 'Actif', href: '/administration/modules' }));
  return { chargement: modules.isPending, erreur: modules.error, lignes: modules.data ? lignes : null };
}

/** Modules actifs : liste des modules (administrateurs), sinon ceux annoncés par GET /contexte ; null si inconnus. */
export function useModulesActifs(): Set<string> | null {
  const { contexte } = useSession();
  const modules = useModules();
  if (modules.data) return new Set(modules.data.filter((m) => m.actif).map((m) => m.module));
  const annonces = contexte?.appartenance_active?.modules_actifs;
  return annonces ? new Set(annonces) : null;
}
